import React, { useEffect, useState } from 'react';
import { createSignedPortalUrl, isStorageUrl } from '@/lib/portalStorage';

/* ---------------------------------------------------------------------------
 * Proposal photo resolver.
 *
 * The portal-photos bucket is PRIVATE (migration 021), so the public URL saved
 * on an image block no longer resolves — it 404s and the <img> breaks. In an
 * authenticated session (the contractor building / previewing / printing a
 * proposal) we can mint a short-lived SIGNED url from the stored path, which
 * migration 021 explicitly allows ("so the dashboard can sign/preview").
 *
 * Resolution order:
 *   1. If a storage `path` is present, try a signed url.
 *   2. Otherwise (or on failure) fall back to the stored `url` — which still
 *      works for legacy data-URL photos and public buckets. Anonymous portal
 *      viewers fall here; wiring signed urls for them is the Phase-3 job.
 * ------------------------------------------------------------------------- */
export const useResolvedImageUrl = ({ url, path }) => {
  const [src, setSrc] = useState(url || '');

  useEffect(() => {
    let alive = true;
    const fallback = url || '';

    // Prefer signing from an explicit path, or from a storage public URL we
    // can still recognize (older blocks saved only the url).
    const storagePath =
      path ||
      (isStorageUrl(url)
        ? url.split('/storage/v1/object/public/portal-photos/')[1] || ''
        : '');

    if (storagePath) {
      createSignedPortalUrl(storagePath).then((signed) => {
        if (!alive) return;
        setSrc(signed || fallback);
      });
    } else {
      setSrc(fallback);
    }

    return () => {
      alive = false;
    };
  }, [url, path]);

  return src;
};

export const ProposalImage = ({ url, path, alt, className, loading }) => {
  const src = useResolvedImageUrl({ url, path });
  if (!src) return null;
  return (
    <img src={src} alt={alt || ''} className={className} loading={loading} />
  );
};

export default ProposalImage;
